import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';

interface Product {
  id: number;
  name: string;
  facility_id: number;
}

interface AdverseEvent {
  id?: number;
  product_id: number;
  description: string;
  severity: string;
  received_date?: string;
  
  // Patient Information
  patient_age?: number;
  patient_gender?: string;
  patient_weight?: number;
  patient_height?: number;
  
  // Event Details
  event_date?: string;
  event_location?: string;
  event_outcome?: string;
  
  // SAE Classification
  is_serious?: boolean;
  is_adverse_event?: boolean;
  is_serious_adverse_event?: boolean;
  sae_deadline?: string;
  
  // SAE Criteria
  death: boolean;
  life_threatening: boolean;
  hospitalization: boolean;
  significant_disability: boolean;
  congenital_anomaly: boolean;
  required_intervention: boolean;
  significant_disfigurement?: boolean;
  infection?: boolean;
  
  // Medical Information
  medical_history?: string;
  concomitant_medications?: string;
  allergies?: string;
  
  // Reporter Information
  reported_by?: string;
  reporter_contact?: string;
  report_source: string;
  
  // Product Information
  lot_number?: string;
  expiration_date?: string;
  product_usage?: string;
  
  // Investigation & Follow-up
  investigation_status?: string;
  follow_up_required?: boolean;
  follow_up_date?: string;
  
  // FDA Reporting
  fda_report_status?: string;
  fda_report_date?: string;
  fda_report_id?: string;
  fda_acknowledgment_date?: string;
  
  // Internal Status
  status?: string;
  priority?: string;
  
  // Additional Fields
  notes?: string;
}

interface AdverseEventFormProps {
  onSubmit: (ae: AdverseEvent) => Promise<void>;
  initialData?: AdverseEvent;
}

const AdverseEventForm: React.FC<AdverseEventFormProps> = ({ onSubmit, initialData }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState<AdverseEvent>({
    product_id: initialData?.product_id || 0,
    description: initialData?.description || '',
    severity: initialData?.severity || 'mild',
    received_date: initialData?.received_date || '',
    
    // Patient Information
    patient_age: initialData?.patient_age,
    patient_gender: initialData?.patient_gender || '',
    patient_weight: initialData?.patient_weight,
    patient_height: initialData?.patient_height,
    
    // Event Details
    event_date: initialData?.event_date || '',
    event_location: initialData?.event_location || '',
    event_outcome: initialData?.event_outcome || '',
    
    // SAE Classification
    is_serious: initialData?.is_serious || false,
    is_adverse_event: initialData?.is_adverse_event || false,
    is_serious_adverse_event: initialData?.is_serious_adverse_event || false,
    sae_deadline: initialData?.sae_deadline || '',
    
    // SAE Criteria
    death: initialData?.death || false,
    life_threatening: initialData?.life_threatening || false,
    hospitalization: initialData?.hospitalization || false,
    significant_disability: initialData?.significant_disability || false,
    congenital_anomaly: initialData?.congenital_anomaly || false,
    required_intervention: initialData?.required_intervention || false,
    significant_disfigurement: initialData?.significant_disfigurement || false,
    infection: initialData?.infection || false,
    
    // Medical Information
    medical_history: initialData?.medical_history || '',
    concomitant_medications: initialData?.concomitant_medications || '',
    allergies: initialData?.allergies || '',
    
    // Reporter Information
    reported_by: initialData?.reported_by || '',
    reporter_contact: initialData?.reporter_contact || '',
    report_source: initialData?.report_source || 'consumer',
    
    // Product Information
    lot_number: initialData?.lot_number || '',
    expiration_date: initialData?.expiration_date || '',
    product_usage: initialData?.product_usage || '',
    
    // Investigation & Follow-up
    investigation_status: initialData?.investigation_status || '',
    follow_up_required: initialData?.follow_up_required || false,
    follow_up_date: initialData?.follow_up_date || '',
    
    // FDA Reporting
    fda_report_status: initialData?.fda_report_status || '',
    fda_report_date: initialData?.fda_report_date || '',
    fda_report_id: initialData?.fda_report_id || '',
    fda_acknowledgment_date: initialData?.fda_acknowledgment_date || '',
    
    // Internal Status
    status: initialData?.status || 'pending',
    priority: initialData?.priority || 'normal',
    
    // Additional Fields
    notes: initialData?.notes || '',
  });

  // Centralized API base

  useEffect(() => {
    fetchProducts();
  }, []);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        product_id: initialData.product_id || 0,
        description: initialData.description || '',
        severity: initialData.severity || 'mild',
        received_date: initialData.received_date || '',
        
        // Patient Information
        patient_age: initialData.patient_age,
        patient_gender: initialData.patient_gender || '',
        patient_weight: initialData.patient_weight,
        patient_height: initialData.patient_height,
        
        // Event Details
        event_date: initialData.event_date || '',
        event_location: initialData.event_location || '',
        event_outcome: initialData.event_outcome || '',
        
        // SAE Classification
        is_serious: initialData.is_serious || false,
        is_adverse_event: initialData.is_adverse_event || false,
        is_serious_adverse_event: initialData.is_serious_adverse_event || false,
        sae_deadline: initialData.sae_deadline || '',
        
        // SAE Criteria
        death: initialData.death || false,
        life_threatening: initialData.life_threatening || false,
        hospitalization: initialData.hospitalization || false,
        significant_disability: initialData.significant_disability || false,
        congenital_anomaly: initialData.congenital_anomaly || false,
        required_intervention: initialData.required_intervention || false,
        significant_disfigurement: initialData.significant_disfigurement || false,
        infection: initialData.infection || false,
        
        // Medical Information
        medical_history: initialData.medical_history || '',
        concomitant_medications: initialData.concomitant_medications || '',
        allergies: initialData.allergies || '',
        
        // Reporter Information
        reported_by: initialData.reported_by || '',
        reporter_contact: initialData.reporter_contact || '',
        report_source: initialData.report_source || 'consumer',
        
        // Product Information
        lot_number: initialData.lot_number || '',
        expiration_date: initialData.expiration_date || '',
        product_usage: initialData.product_usage || '',
        
        // Investigation & Follow-up
        investigation_status: initialData.investigation_status || '',
        follow_up_required: initialData.follow_up_required || false,
        follow_up_date: initialData.follow_up_date || '',
        
        // FDA Reporting
        fda_report_status: initialData.fda_report_status || '',
        fda_report_date: initialData.fda_report_date || '',
        fda_report_id: initialData.fda_report_id || '',
        fda_acknowledgment_date: initialData.fda_acknowledgment_date || '',
        
        // Internal Status
        status: initialData.status || 'pending',
        priority: initialData.priority || 'normal',
        
        // Additional Fields
        notes: initialData.notes || '',
      });
    }
  }, [initialData]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE}/products/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For edit mode, only send fields that have actually changed
    if (initialData) {
      const changedData: any = {};
      
      // Compare each field and only include changed ones
      Object.keys(formData).forEach(key => {
        const formValue = formData[key as keyof AdverseEvent];
        const initialValue = initialData[key as keyof AdverseEvent];
        
        // For boolean fields, only include if they're explicitly true
        if (typeof formValue === 'boolean') {
          if (formValue !== (initialValue || false)) {
            changedData[key] = formValue;
          }
        }
        // For other fields, include if they're different
        else if (formValue !== (initialValue || '')) {
          changedData[key] = formValue;
        }
      });
      
      // Always include the ID for updates
      changedData.id = initialData.id;
      
      await onSubmit(changedData as AdverseEvent);
    } else {
      // For create mode, send all data
      await onSubmit(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'product_id' || name === 'patient_age' || name === 'patient_weight' || name === 'patient_height') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : undefined
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const isSAE = () => {
    return formData.death || formData.life_threatening || formData.hospitalization || 
           formData.significant_disability || formData.congenital_anomaly || formData.required_intervention ||
           !!formData.significant_disfigurement || !!formData.infection;
  };

  const getSAECount = () => {
    return [formData.death, formData.life_threatening, formData.hospitalization, 
            formData.significant_disability, formData.congenital_anomaly, formData.required_intervention,
            formData.significant_disfigurement, formData.infection]
           .filter(Boolean).length;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Terminology Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Understanding Adverse Events (AEs) and Serious Adverse Events (SAEs) - MoCRA Compliant</h3>
        <div className="text-blue-800 space-y-2 text-sm">
          <p><strong>Adverse Event (AE):</strong> Any untoward medical occurrence associated with the use of a cosmetic product, whether or not considered related to the product.</p>
          <p><strong>Serious Adverse Event (SAE):</strong> An adverse event that results in ANY of the following specific outcomes (MoCRA criteria):</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Death</strong> - Patient dies as a result of the event</li>
            <li><strong>Life-threatening</strong> - Event places patient at immediate risk of death</li>
            <li><strong>Hospitalization</strong> - Initial or prolonged hospitalization required</li>
            <li><strong>Significant disability</strong> - Persistent or significant disability/incapacity</li>
            <li><strong>Congenital anomaly</strong> - Birth defect in offspring</li>
            <li><strong>Required intervention</strong> - Medical intervention needed to prevent permanent impairment</li>
            <li><strong>Significant disfigurement</strong> - Permanent disfigurement (MoCRA specific)</li>
            <li><strong>Infection</strong> - Serious infection requiring intervention (MoCRA specific)</li>
          </ul>
          <div className="bg-yellow-100 border border-yellow-300 rounded p-2 mt-2">
            <p className="text-yellow-800 font-semibold">📋 Important:</p>
            <p className="text-yellow-700">• <strong>Severity alone does NOT determine SAE status</strong> - only the specific criteria above</p>
            <p className="text-yellow-700">• A "severe" event is NOT automatically an SAE unless it meets one of the criteria above</p>
            <p className="text-yellow-700">• SAE status is determined by checking the boxes below, not by severity level</p>
          </div>
          <p className="font-semibold text-red-700">⚠️ SAEs must be reported to FDA within 15 business days!</p>
        </div>
      </div>

      {/* Basic Event Information */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900">Product *</label>
            <select
              name="product_id"
              value={formData.product_id}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select a product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Severity *</label>
            <select
              name="severity"
              value={formData.severity}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900">Event Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Describe the adverse event in detail..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Event Date</label>
            <input
              type="date"
              name="event_date"
              value={formData.event_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Received Date</label>
            <input
              type="date"
              name="received_date"
              value={formData.received_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Event Location</label>
            <input
              type="text"
              name="event_location"
              value={formData.event_location}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="e.g., face, hands, body"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Event Outcome</label>
            <select
              name="event_outcome"
              value={formData.event_outcome}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select outcome</option>
              <option value="recovered">Recovered</option>
              <option value="recovering">Recovering</option>
              <option value="not_recovered">Not Recovered</option>
              <option value="fatal">Fatal</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
        </div>
      </div>

      {/* Patient Information */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Patient Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900">Age</label>
            <input
              type="number"
              name="patient_age"
              value={formData.patient_age || ''}
              onChange={handleChange}
              min="0"
              max="120"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Gender</label>
            <select
              name="patient_gender"
              value={formData.patient_gender}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Weight (kg)</label>
            <input
              type="number"
              name="patient_weight"
              value={formData.patient_weight || ''}
              onChange={handleChange}
              step="0.1"
              min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Height (cm)</label>
            <input
              type="number"
              name="patient_height"
              value={formData.patient_height || ''}
              onChange={handleChange}
              step="0.1"
              min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900">Medical History</label>
            <textarea
              name="medical_history"
              value={formData.medical_history}
              onChange={handleChange}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Relevant medical history..."
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900">Concomitant Medications</label>
            <textarea
              name="concomitant_medications"
              value={formData.concomitant_medications}
              onChange={handleChange}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Other medications being taken..."
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900">Allergies</label>
            <textarea
              name="allergies"
              value={formData.allergies}
              onChange={handleChange}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Known allergies..."
            />
          </div>
        </div>
      </div>

      {/* SAE Criteria */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Serious Adverse Event Criteria</h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="death"
              name="death"
              checked={formData.death}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="death" className="ml-2 block text-sm text-gray-900">
              Death
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="life_threatening"
              name="life_threatening"
              checked={formData.life_threatening}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="life_threatening" className="ml-2 block text-sm text-gray-900">
              Life-threatening
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="hospitalization"
              name="hospitalization"
              checked={formData.hospitalization}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="hospitalization" className="ml-2 block text-sm text-gray-900">
              Hospitalization
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="significant_disability"
              name="significant_disability"
              checked={formData.significant_disability}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="significant_disability" className="ml-2 block text-sm text-gray-900">
              Significant disability
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="congenital_anomaly"
              name="congenital_anomaly"
              checked={formData.congenital_anomaly}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="congenital_anomaly" className="ml-2 block text-sm text-gray-900">
              Congenital anomaly
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="required_intervention"
              name="required_intervention"
              checked={formData.required_intervention}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="required_intervention" className="ml-2 block text-sm text-gray-900">
              Required intervention to prevent permanent impairment
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="significant_disfigurement"
              name="significant_disfigurement"
              checked={!!formData.significant_disfigurement}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="significant_disfigurement" className="ml-2 block text-sm text-gray-900">
              Significant disfigurement
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="infection"
              name="infection"
              checked={!!formData.infection}
              onChange={handleChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="infection" className="ml-2 block text-sm text-gray-900">
              Infection requiring intervention
            </label>
          </div>
        </div>
      </div>

      {/* Reporter Information */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reporter Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900">Reported By</label>
            <input
              type="text"
              name="reported_by"
              value={formData.reported_by}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Name of person reporting"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Contact Information</label>
            <input
              type="text"
              name="reporter_contact"
              value={formData.reporter_contact}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Phone or email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Report Source</label>
            <select
              name="report_source"
              value={formData.report_source}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="consumer">Consumer</option>
              <option value="healthcare_professional">Healthcare Professional</option>
              <option value="manufacturer">Manufacturer</option>
              <option value="distributor">Distributor</option>
            </select>
          </div>
        </div>
      </div>

      {/* Product Information */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900">Lot Number</label>
            <input
              type="text"
              name="lot_number"
              value={formData.lot_number}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Product lot number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900">Expiration Date</label>
            <input
              type="date"
              name="expiration_date"
              value={formData.expiration_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900">Product Usage</label>
            <textarea
              name="product_usage"
              value={formData.product_usage}
              onChange={handleChange}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="How the product was used..."
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="Additional information or notes..."
        />
      </div>

      {/* SAE Warning */}
      {isSAE() && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-red-800">
                Serious Adverse Event Detected ({getSAECount()} criteria met)
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>This event meets FDA criteria for a Serious Adverse Event (SAE). It must be reported to the FDA within 15 business days.</p>
                <p className="mt-1 font-semibold">Missing this deadline could result in significant penalties.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="submit"
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Submit Report
        </button>
      </div>
    </form>
  );
};

export default AdverseEventForm; 